// src/components/ERDEditor/LeftPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MdHistory, MdFolder, MdWarning, MdSend, MdGrading } from 'react-icons/md';
import RawEditor from './RawEditor';
import VisualEditor from './VisualEditor';
import ERDVisualization from './ERDVisualization';
import SaveIndicator from './SaveIndicator';
import { useSaveManager } from './hooks/useSaveManager';
import SubmissionModal from '../Submissions/SubmissionModal';
import { createSubmission, submitSubmission } from '../../utils/api/AssignmentsApi';

const LeftPanel = ({ erdState, resizing, username, apiBaseUrl, userRole, onOpenVersionSidebar, getClassContext, showRubric = false, onOpenRubric = null, showSubmissions = false, onOpenSubmissions = null, assignmentId = null, componentId = null, assignmentData = null }) => {
  // Modal state for None environment warning
  const [showNoneWarning, setShowNoneWarning] = useState(false);
  // Modal state for submission
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  // Submission in progress
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Requirements state
  const [viewMode, setViewMode] = useState(null); // null = follow erdState.editorMode, 'requirements' = requirements tab, 'guideline' = guideline tab
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsDirty, setRequirementsDirty] = useState(false);
  const [requirementsSaving, setRequirementsSaving] = useState(false);

  // AI Guideline state
  const [guidelineContent, setGuidelineContent] = useState('');
  const [guidelineLoading, setGuidelineLoading] = useState(false);
  const [guidelineSaving, setGuidelineSaving] = useState(false);
  const [guidelineSaved, setGuidelineSaved] = useState(false);
  const savedRequirementsRef = useRef([]);
  const importFileRef = useRef(null);

  // Initialize save manager
  const {
    saveStatus,
    lastSaved,
    saveError,
    eventSave,
  } = useSaveManager(username, erdState, getClassContext);

  // Always-fresh refs — updated synchronously during render so child effects
  // (EntityForm, RelationshipForm, etc.) always read the latest values.
  const generateERDRef = useRef(erdState.generateERD);
  const eventSaveRef = useRef(eventSave);
  const svgContentRef = useRef(erdState.svgContent);
  const isGeneratingRef = useRef(erdState.isGenerating);
  generateERDRef.current = erdState.generateERD;
  eventSaveRef.current = eventSave;
  svgContentRef.current = erdState.svgContent;
  isGeneratingRef.current = erdState.isGenerating;

  // Queues a regeneration while a previous one is still in-flight.
  const pendingRegenerateRef = useRef(false);

  // Wrapped onSaveEvent: save + auto-regenerate when a form submits a change.
  // If a generation is already running, we queue one so it fires as soon as
  // the current generation completes.
  const handleFormSaveEvent = useCallback((eventType, customData = {}) => {
    console.log('[handleFormSaveEvent] called:', eventType, {
      hasSvg: !!svgContentRef.current,
      isGenerating: isGeneratingRef.current,
      hasGenerateFn: !!generateERDRef.current,
    });
    eventSaveRef.current(eventType, customData);
    if (!svgContentRef.current) {
      console.log('[handleFormSaveEvent] skipping generateERD — no existing SVG');
      return;
    }
    if (isGeneratingRef.current) {
      console.log('[handleFormSaveEvent] generation in progress — queuing regeneration');
      pendingRegenerateRef.current = true;
    } else {
      console.log('[handleFormSaveEvent] calling generateERD now');
      generateERDRef.current(eventSaveRef.current);
    }
  }, []);

  // Fire any queued regeneration once isGenerating transitions to false.
  useEffect(() => {
    console.log('[isGenerating effect]', { isGenerating: erdState.isGenerating, pending: pendingRegenerateRef.current });
    if (!erdState.isGenerating && pendingRegenerateRef.current) {
      pendingRegenerateRef.current = false;
      if (svgContentRef.current) {
        console.log('[isGenerating effect] firing queued generateERD');
        generateERDRef.current(eventSaveRef.current);
      } else {
        console.log('[isGenerating effect] queued but no SVG — skipping');
      }
    }
  }, [erdState.isGenerating]);

  // Compute due date status from assignment data
  const getDueDateStatus = () => {
    if (!assignmentData) return { canSubmit: true, isLate: false, isLocked: false };
    const now = new Date();
    const dueDate = assignmentData.due_date ? new Date(assignmentData.due_date) : null;
    if (!dueDate || now <= dueDate) return { canSubmit: true, isLate: false, isLocked: false };
    // Past due date
    if (assignmentData.allow_late_submission) {
      const lateDueDate = assignmentData.late_due_date ? new Date(assignmentData.late_due_date) : null;
      if (!lateDueDate || now <= lateDueDate) return { canSubmit: true, isLate: true, isLocked: false };
      return { canSubmit: false, isLate: true, isLocked: true };
    }
    if (assignmentData.lock_after_due_date) return { canSubmit: false, isLate: true, isLocked: true };
    return { canSubmit: true, isLate: true, isLocked: false };
  };

  const dueDateStatus = getDueDateStatus();

  // Handle environment change with validation
  const handleEnvironmentChange = (newEnvironment) => {
    if (newEnvironment === 'None') {
      setShowNoneWarning(true);
    }
    erdState.setSelectedEnvironment(newEnvironment);
    eventSave('environment_changed', { environment: newEnvironment });
  };

  // Enhanced reset function that clears both raw and visual editor states
  const handleReset = () => {
    // Clear raw editor state
    erdState.handleReset();

    // Clear visual editor state if the function exists
    if (erdState.clearVisualEditorState) {
      erdState.clearVisualEditorState();
    }
  };

  // Handle opening submission modal
  const handleOpenSubmitModal = () => {
    // Check if environment is selected and not "None"
    if (erdState.selectedEnvironment === 'None') {
      alert('Please select an environment before submitting');
      return;
    }

    // Check if there's ERD content to submit
    if (!erdState.inputText || !erdState.inputText.trim()) {
      alert('Please create an ERD before submitting');
      return;
    }

    // Open the submission modal
    setShowSubmissionModal(true);
  };

  // Handle closing submission modal
  const handleCloseSubmitModal = () => {
    setShowSubmissionModal(false);
  };

  // Handle assignment submission - always creates a new submission
  const handleSubmitAssignment = async (submissionData) => {
    const classContext = getClassContext();
    if (!classContext || !assignmentId) {
      alert('No assignment context available');
      return false;
    }

    setIsSubmitting(true);
    try {
      // Always create a new submission (new attempt)
      const createResult = await createSubmission({
        assignment_id: assignmentId,
        submission_type: 'erd',
        content: {
          notation: submissionData.erdNotation,
          svg: submissionData.svgContent,
          erd_data: submissionData.erdData,
          environment: submissionData.environment
        },
        status: 'draft'
      }, classContext);

      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create submission');
      }

      // Mark as submitted
      const submitResult = await submitSubmission(createResult.submission_id, classContext);
      if (!submitResult.success) {
        throw new Error(submitResult.error || 'Failed to submit');
      }

      return true;
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };


  // Load ERD AI guideline once on mount (global per class, not per environment)
  useEffect(() => {
    if (isStudent) return;
    const classContext = getClassContext();
    if (!classContext?.class_id) return;
    setGuidelineLoading(true);
    fetch('/api/document-editor/ai-guideline?component=erd', {
      headers: { 'Content-Type': 'application/json', 'Class-ID': classContext.class_id },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => setGuidelineContent(data.guideline?.content || ''))
      .catch(() => {})
      .finally(() => setGuidelineLoading(false));
  }, []);

  const handleSaveGuideline = async () => {
    const classContext = getClassContext();
    if (!classContext?.class_id) return;
    setGuidelineSaving(true);
    try {
      await fetch('/api/document-editor/ai-guideline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Class-ID': classContext.class_id },
        credentials: 'include',
        body: JSON.stringify({ component: 'erd', title: 'ERD AI Guideline', content: guidelineContent }),
      });
      setGuidelineSaved(true);
      setTimeout(() => setGuidelineSaved(false), 2000);
    } catch (e) { console.error('Save guideline failed', e); }
    finally { setGuidelineSaving(false); }
  };

  // Fetch requirements when environment changes
  useEffect(() => {
    const env = erdState.selectedEnvironment;
    if (!env || env === 'None') {
      setRequirements([]);
      savedRequirementsRef.current = [];
      return;
    }
    const classContext = getClassContext();
    if (!classContext?.class_code) return;

    setRequirementsLoading(true);
    fetch(`/api/requirements/${env}`, {
      headers: { 'Content-Type': 'application/json', 'Class-Code': classContext.class_code },
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        const fetched = data?.requirements || [];
        setRequirements(fetched);
        savedRequirementsRef.current = fetched;
      })
      .catch(() => {})
      .finally(() => setRequirementsLoading(false));
  }, [erdState.selectedEnvironment, getClassContext]);

  // Track dirty state
  useEffect(() => {
    if (requirementsLoading) return;
    const saved = savedRequirementsRef.current;
    const dirty = requirements.length !== saved.length ||
      requirements.some((r, i) => r !== saved[i]);
    setRequirementsDirty(dirty);
  }, [requirements, requirementsLoading]);

  // Save requirements
  const saveRequirements = async (reqs) => {
    const env = erdState.selectedEnvironment;
    const classContext = getClassContext();
    if (!env || env === 'None' || !classContext?.class_code) return;
    setRequirementsSaving(true);
    try {
      const response = await fetch(`/api/requirements/${env}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Class-Code': classContext.class_code },
        credentials: 'include',
        body: JSON.stringify({
          version: `inline_${Date.now()}`,
          requirements: reqs.filter(r => r.trim() !== '')
        })
      });
      if (response.ok) {
        const cleaned = reqs.filter(r => r.trim() !== '');
        setRequirements(cleaned);
        savedRequirementsRef.current = cleaned;
      }
    } catch (err) {
      console.warn('[LeftPanel] Save requirements failed:', err);
    } finally {
      setRequirementsSaving(false);
    }
  };

  // Export requirements as JSON
  const handleExportJSON = async () => {
    const env = erdState.selectedEnvironment;
    const classContext = getClassContext();
    if (!env || env === 'None' || !classContext?.class_code) return;
    try {
      const response = await fetch(`/api/requirements/${env}/export?format=json`, {
        headers: { 'Class-Code': classContext.class_code },
        credentials: 'include'
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requirements_${env}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[LeftPanel] Export failed:', err);
    }
  };

  // Export full environment
  const handleExportFull = async () => {
    const env = erdState.selectedEnvironment;
    const classContext = getClassContext();
    if (!env || env === 'None' || !classContext?.class_code) return;
    try {
      const response = await fetch(`/api/requirements/${env}/export-full`, {
        headers: { 'Class-Code': classContext.class_code },
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `environment_${env}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[LeftPanel] Full export failed:', err);
    }
  };

  // Import requirements from JSON file
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    const env = erdState.selectedEnvironment;
    const classContext = getClassContext();
    if (!file || !env || env === 'None' || !classContext?.class_code) return;
    e.target.value = '';

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const reqs = data.requirements || data;
      const importedRequirements = Array.isArray(reqs)
        ? reqs.map(r => (typeof r === 'string' ? r : r.description || ''))
        : [];

      if (importedRequirements.length === 0) {
        console.warn('[LeftPanel] No requirements found in imported file');
        return;
      }

      const response = await fetch(`/api/requirements/${env}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Class-Code': classContext.class_code },
        credentials: 'include',
        body: JSON.stringify({ version: 'imported', requirements: importedRequirements })
      });
      if (response.ok) {
        setRequirements(importedRequirements);
        savedRequirementsRef.current = importedRequirements;
      }
    } catch (err) {
      console.warn('[LeftPanel] Import failed:', err);
    }
  };

  const isStudent = userRole === 'Student';

  const activeTab = viewMode || erdState.editorMode; // 'raw', 'visual', or 'requirements'

  return (
    <>
      <div
        className="flex flex-col border-r border-gray-200 min-h-0"
        style={{ width: `${erdState.leftPanelWidth}%` }}
      >
        {/* Header - Fixed at top */}
        <div className="p-6 flex-shrink-0 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">ERD Editor</h2>
            <div className="flex gap-2 items-center">
              {/* Environment Selection */}
              <div className="flex items-center gap-2">
                <MdFolder className="w-4 h-4 text-gray-600" />
                <select
                  value={erdState.selectedEnvironment}
                  onChange={(e) => handleEnvironmentChange(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  {erdState.environments.map((env) => (
                    <option key={env} value={env}>
                      {env}
                    </option>
                  ))}
                </select>
              </div>
              {showRubric && onOpenRubric && (
                <button
                  onClick={onOpenRubric}
                  className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors duration-200"
                  title="View Rubric"
                >
                  <MdGrading className="w-5 h-5" />
                </button>
              )}
              {showSubmissions && onOpenSubmissions && (
                <button
                  onClick={onOpenSubmissions}
                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                  title="View My Submissions"
                >
                  <MdHistory className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onOpenVersionSidebar}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center gap-2"
              >
                <MdHistory className="w-4 h-4" />
                Versions
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors duration-200"
                >
                  Reset
                </button>
                {assignmentId && (
                  <button
                    onClick={handleOpenSubmitModal}
                    disabled={erdState.selectedEnvironment === 'None' || !erdState.inputText || isSubmitting || dueDateStatus.isLocked}
                    className={`${
                      dueDateStatus.isLocked
                        ? 'bg-gray-400 cursor-not-allowed'
                        : dueDateStatus.isLate
                          ? 'bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed'
                          : 'bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed'
                    } text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center gap-2`}
                    title={dueDateStatus.isLocked ? 'Submissions are closed' : ''}
                  >
                    {isSubmitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <MdSend className="w-4 h-4" />
                    )}
                    {dueDateStatus.isLocked ? 'Closed' : dueDateStatus.isLate ? 'Submit Late' : 'Submit'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Save Indicator */}
          <div className="mb-4">
            <SaveIndicator
              saveStatus={saveStatus}
              lastSaved={lastSaved}
              error={saveError}
              environment={erdState.selectedEnvironment}
            />
          </div>

          {/* Editor Mode Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => { erdState.setEditorMode('raw'); setViewMode(null); }}
              className={`px-4 py-2 font-medium text-sm ${activeTab === 'raw'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Raw Notation
            </button>
            <button
              onClick={() => { erdState.setEditorMode('visual'); setViewMode(null); }}
              className={`px-4 py-2 font-medium text-sm ${activeTab === 'visual'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Visual Editor
            </button>
            {erdState.selectedEnvironment && erdState.selectedEnvironment !== 'None' && (
              <button
                onClick={() => setViewMode('requirements')}
                className={`px-4 py-2 font-medium text-sm ${activeTab === 'requirements'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Requirements
              </button>
            )}
            {!isStudent && (
              <button
                onClick={() => setViewMode('guideline')}
                className={`px-4 py-2 font-medium text-sm ${activeTab === 'guideline'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                AI Guideline
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {activeTab === 'guideline' ? (
            /* AI Guideline Panel */
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ marginBottom: 12 }}>
                <h3 style={{ fontWeight: 600, fontSize: 14, color: '#4f46e5', marginBottom: 4 }}>
                  ERD AI Guideline
                </h3>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                  Rules passed to the LLM when generating ERD feedback. Applies to all environments. Supports Markdown.
                </p>
              </div>
              {guidelineLoading ? (
                <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
              ) : (
                <textarea
                  value={guidelineContent}
                  onChange={e => setGuidelineContent(e.target.value)}
                  placeholder={'# ERD Feedback Rules\n\n- Rule 1\n- Rule 2\n\n# Scaffold Policy\n...'}
                  style={{
                    flex: 1, width: '100%', minHeight: 400, fontFamily: 'monospace',
                    fontSize: 13, lineHeight: 1.6, padding: '10px 12px',
                    border: '1px solid #d1d5db', borderRadius: 6,
                    resize: 'vertical', boxSizing: 'border-box', color: '#1e293b',
                  }}
                />
              )}
              <button
                onClick={handleSaveGuideline}
                disabled={guidelineSaving}
                style={{
                  marginTop: 12, padding: '8px 18px', background: guidelineSaved ? '#22c55e' : '#4f46e5',
                  color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, alignSelf: 'flex-end',
                  transition: 'background 0.2s',
                }}
              >
                {guidelineSaving ? 'Saving…' : guidelineSaved ? '✓ Saved' : 'Save Guideline'}
              </button>
            </div>
          ) : activeTab === 'requirements' ? (
            /* Requirements Panel */
            <>
              {/* Export / Import toolbar — instructors only */}
              {!isStudent && (
                <div className="flex flex-wrap items-center gap-2 mb-5 pb-4 border-b border-gray-200">
                  <span className="text-xs font-medium text-gray-500 mr-1">Export:</span>
                  <button
                    onClick={handleExportJSON}
                    className="px-3 py-1 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    Requirements JSON
                  </button>
                  <button
                    onClick={handleExportFull}
                    className="px-3 py-1 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    Full Environment JSON
                  </button>
                  <span className="text-gray-300 mx-1">|</span>
                  <span className="text-xs font-medium text-gray-500">Import:</span>
                  <label className="px-3 py-1 text-xs font-medium border border-blue-500 text-blue-600 rounded hover:bg-blue-50 cursor-pointer transition-colors">
                    Load File
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".json"
                      onChange={handleImport}
                      className="sr-only"
                    />
                  </label>
                </div>
              )}

              {requirementsLoading ? (
                <p className="text-sm text-gray-500">Loading requirements...</p>
              ) : (
                <div className="space-y-3">
                  {requirements.map((req, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center mt-0.5">
                        {index + 1}
                      </span>
                      {isStudent ? (
                        <p className="text-sm text-gray-800 leading-relaxed">{req}</p>
                      ) : (
                        <div className="flex-1 flex items-start gap-2">
                          <textarea
                            value={req}
                            onChange={(e) => {
                              const updated = [...requirements];
                              updated[index] = e.target.value;
                              setRequirements(updated);
                            }}
                            onBlur={() => saveRequirements(requirements)}
                            rows={Math.max(1, req.split('\n').length)}
                            className="flex-1 text-sm text-gray-800 border border-transparent rounded px-2 py-1 hover:border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none outline-none bg-transparent"
                          />
                          <button
                            onClick={() => {
                              const updated = requirements.filter((_, i) => i !== index);
                              setRequirements(updated);
                              saveRequirements(updated);
                            }}
                            className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1"
                            title="Remove requirement"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add new requirement — instructors only */}
                  {!isStudent && (
                    <div className="flex items-start gap-2 mt-4">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-400 text-sm font-semibold flex items-center justify-center mt-0.5">
                        +
                      </span>
                      <button
                        onClick={() => setRequirements(prev => [...prev, ''])}
                        className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        Add requirement
                      </button>
                    </div>
                  )}

                  {requirements.length === 0 && !isStudent && (
                    <p className="text-sm text-gray-400 italic">No requirements yet. Add one above.</p>
                  )}
                  {requirements.length === 0 && isStudent && (
                    <p className="text-sm text-gray-500 italic">No requirements defined for this environment.</p>
                  )}
                </div>
              )}

              {/* Save button */}
              {!isStudent && requirementsDirty && (
                <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Unsaved changes</span>
                  <button
                    onClick={() => saveRequirements(requirements)}
                    disabled={requirementsSaving}
                    className={`px-4 py-1.5 text-xs font-medium rounded transition-colors ${
                      requirementsSaving
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {requirementsSaving ? 'Saving...' : 'Save Requirements'}
                  </button>
                </div>
              )}
            </>
          ) : (
            /* ERD Editor Content */
            <>
              {erdState.editorMode === 'raw' ? (
                <RawEditor
                  erdState={erdState}
                  resizing={resizing}
                  onSaveEvent={eventSave}
                />
              ) : (
                <VisualEditor
                  erdState={erdState}
                  onSaveEvent={handleFormSaveEvent}
                />
              )}

              {/* ERD Visualization */}
              {erdState.svgContent && (
                <div className="mt-6">
                  <ERDVisualization
                    svgContent={erdState.svgContent}
                    erdNotation={erdState.inputText}
                    username={username}
                    environment={erdState.selectedEnvironment}
                    onSaveEvent={eventSave}
                  />
                </div>
              )}

              {/* Bottom Actions */}
              <div className="p-6 border-t border-gray-200 flex-shrink-0">
                {/* Relationship Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select relationship:
                  </label>
                  <select
                    value={erdState.selectedRelationship}
                    onChange={(e) => erdState.setSelectedRelationship(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="All">All</option>
                    {erdState.relationships.map((rel, index) => (
                      <option key={index} value={rel.name}>
                        {rel.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Generate ERD Button */}
                <button
                  onClick={() => {
                    erdState.generateERD(eventSave);
                  }}
                  disabled={erdState.isGenerating}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded-md transition-colors duration-200"
                >
                  {erdState.isGenerating ? 'Generating...' : 'Generate ERD'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* None Environment Warning Modal */}
      {showNoneWarning && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-md w-full">
              <div className="flex items-center mb-4">
                <MdWarning className="w-6 h-6 text-yellow-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Warning: No Saves
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                You have selected "None" as your environment. Your changes will not be saved automatically.
                Please select a valid environment to enable saving.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowNoneWarning(false)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors duration-200"
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Submission Modal */}
      {showSubmissionModal && (
        <SubmissionModal
          isOpen={showSubmissionModal}
          onClose={handleCloseSubmitModal}
          submissionType="erd"
          erdNotation={erdState.inputText}
          erdData={erdState.parsedData}
          svgContent={erdState.svgContent}
          assignmentName={erdState.selectedEnvironment}
          username={username}
          onSubmit={handleSubmitAssignment}
        />
      )}

    </>
  );
};

export default LeftPanel;